import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MemeGallery from '../Meme/MemeGallery';
import MemeCreator from '../Meme/MemeCreator';
import { server } from '../../server';
import LifeEvents from '../LifeEvent/LifeEvents';
import CreateMemePrompt from '../Meme/CreateMemePrompt';
import '../../stylesheets/ProfileContentCarouselGuardrails.css';
import ProfileFeatureWidgetActionMenu from './ProfileFeatureWidgetActionMenu';
import SharedRightSlideout from '../Layout/SharedRightSlideout';


const MyContentCarousel = ({ ownerId, profileType, canCreate = false, showActionMenu = false }) => {
  const items = [
    
    {
      type: 'meme', 
    },
    {
      type: 'lifeEvent', 
    },
    {
      type: 'video',
      videoSrc: "https://player.vimeo.com/video/1003216849?autoplay=0&loop=1&title=0&byline=0&portrait=0"
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [memes, setMemes] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMemeActionMenuOpen, setIsMemeActionMenuOpen] = useState(false);

  // Function to fetch memes
  const fetchMemes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${server}/meme/user-memes`, {
        params: { ownerId, profileType }, 
        withCredentials: true,
      });
      setMemes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching memes:', error);
      setError('Failed to fetch memes. Please try again.');
      setLoading(false);
    }
  };

  // Fetch memes on component mount and when ownerId or profileType changes
  useEffect(() => {
    if (ownerId && profileType) {
      fetchMemes();
    }
  }, [ownerId, profileType]);

  useEffect(() => {
    setIsMemeActionMenuOpen(false);
  }, [currentIndex, ownerId, profileType]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? items.length - 1 : prevIndex - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === items.length - 1 ? 0 : prevIndex + 1));
  };

  const handleOpenModal = () => {
    if (!canCreate) {
      return;
    }

    setIsMemeActionMenuOpen(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    fetchMemes(); // Refetch memes in case a new meme was created
  };

  // Determine if a meme exists
  const hasMeme = memes.length > 0;
  const activeMeme = hasMeme ? memes[0] : null;

  const handleDeleteMeme = async () => {
    if (!canCreate || !activeMeme?._id) {
      return;
    }

    try {
      setIsMemeActionMenuOpen(false);
      await axios.delete(`${server}/meme/${activeMeme._id}`, {
        params: { ownerId, profileType },
        withCredentials: true,
      });
      setMemes((prevMemes) => prevMemes.filter((meme) => meme._id !== activeMeme._id));
    } catch (error) {
      console.error('Error deleting meme:', error);
      setError('Failed to delete meme. Please try again.');
    }
  };

  // Adjust font size based on image width
  const adjustFontSize = useCallback((img, meme) => {
    if (img) {
      const fontSizeInPixels = (meme.fontSize / 100) * img.offsetWidth;
      if (img.nextSibling) {
        img.nextSibling.style.fontSize = `${fontSizeInPixels}px`;
      }
    }
  }, []);

  // Handle loading and error states
  if (loading) {
    return <div className="carousel-loading">Loading content...</div>;
  }

  if (error) {
    return <div className="carousel-error">{error}</div>;
  }

  return (
    <div className={`my-content-carousel-container profile-content-carousel-widget${canCreate ? ' profile-content-carousel-widget-can-create' : ' profile-content-carousel-widget-readonly'}`}>
      {showActionMenu && (
        <ProfileFeatureWidgetActionMenu
          label="Meme actions"
          actions={[
            {
              label: 'New Meme',
              onClick: handleOpenModal,
              hidden: !canCreate,
            },
            {
              label: 'Edit Meme',
              disabled: true,
              hidden: !canCreate || !hasMeme,
            },
            {
              label: 'Delete Meme',
              onClick: handleDeleteMeme,
              hidden: !canCreate || !hasMeme,
            },
          ]}
        />
      )}
      {/* Carousel Controls */}
      <button onClick={handlePrev} className="my-content-carousel-control left">
        &#10094;
      </button>
      <div className="my-content-carousel-content" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {items.map((item, index) => (
          <div
            key={index}
            className={`my-content-carousel-item ${index === currentIndex ? 'active' : ''}`}
          >
            {/* Video Slide */}
            {item.type === 'video' ? (
              <div className="my-content-carousel-video-container">
                <iframe 
                  src={item.videoSrc}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Video"
                ></iframe>
                 <div className="streaming-coming-soon-overlay">
                 <span>Streaming <br /> Coming Soon</span>
                </div>
              </div>
            ) : item.type === 'meme' ? (
              /* Meme Slide */
              <div className="meme-slide">
                {hasMeme ? (
                  <>
                    {canCreate && <div className="slot-action-menu meme-slot-action-menu">
                      <button
                        type="button"
                        className="slot-action-menu-button"
                        aria-label="Meme actions"
                        aria-expanded={isMemeActionMenuOpen}
                        onClick={() => setIsMemeActionMenuOpen((isOpen) => !isOpen)}
                      >
                        &#8942;
                      </button>
                      {isMemeActionMenuOpen && (
                        <div className="slot-action-menu-list" role="menu">
                          <button type="button" className="slot-action-menu-item" onClick={handleDeleteMeme}>
                            Delete Meme
                          </button>
                          <button type="button" className="slot-action-menu-item" onClick={handleOpenModal}>
                            New Meme
                          </button>
                        </div>
                      )}
                    </div>}
                    <MemeGallery memes={memes} adjustFontSize={adjustFontSize} />
                  </>
                ) : !canCreate ? (
                  <div className="profile-content-carousel-empty-state profile-content-carousel-meme-empty-state">
                    No memes found.
                  </div>
                ) : (
                  <CreateMemePrompt onCreate={handleOpenModal} />
                )}
              </div>
            ) : item.type === 'lifeEvent' ? (
              /* Life Events Slide */
              <div className="life-events-slide">
                <LifeEvents ownerId={ownerId} profileType={profileType} canCreate={canCreate} />
              </div>
            ) : (
              /* Image Slide */
              <div 
                className="my-content-carousel-image-container"
                style={{
                  backgroundImage: `url(${item.backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <div className="my-content-carousel-overlay">
                  <h2 className="my-content-carousel-title">{item.name}</h2>
                  <p className="my-content-carousel-description">{item.description}</p>
                  <a href={item.buttonLink} className="my-content-carousel-button">
                    {item.buttonText}
                  </a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <button onClick={handleNext} className="my-content-carousel-control right">
        &#10095;
      </button>

      <SharedRightSlideout
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Create Meme"
        subtitle="Upload an image, place your text, then publish it to this profile."
        className="meme-create-shared-slideout"
        bodyClassName="meme-create-shared-slideout-body"
      >
        <MemeCreator ownerId={ownerId} profileType={profileType} onClose={handleCloseModal} />
      </SharedRightSlideout>
    </div>
  );
};

export default MyContentCarousel;








